import React from 'react';
import { View, StyleSheet, Linking, TouchableOpacity } from 'react-native';
import { Text } from '@/components/common/text';
import { GlassCard, PersonCell } from '@/components/shared/redesign';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { IResidentDetailsResponse } from '@/types/admin/residentDetails';
import { TransactionHistorySection } from '../common/TransactionHistorySection';
import { formatDateShort } from '@/utils/admin/date';

interface Props {
  resident: Pick<
    IResidentDetailsResponse,
    | 'phoneNumber'
    | 'email'
    | 'roommates'
    | 'accountId'
    | 'leaseId'
    | 'firstName'
    | 'lastName'
    | 'propertyName'
    | 'buildingUnitNumberSpace'
    | 'startDate'
    | 'endDate'
    | 'moveInDate'
    | 'moveOutDate'
    | 'customerType'
    | 'hasAutopay'
    | 'paymentSources'
    | 'recurringPayments'
  >;
  leaseCustomerId?: number;
  leaseId?: number;
  onViewAllTransactions?: () => void;
}

export const ResidentDetailsInfo: React.FC<Props> = ({
  resident,
  leaseCustomerId,
  leaseId,
  onViewAllTransactions,
}) => {
  const styles = useStyles();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  // Equal-height stretching is only safe inside the desktop grid row; in the mobile column it lets Yoga shrink cards below content, which GlassCard then clips.
  const cardStyle = isDesktop ? [styles.card, styles.cardStretch] : styles.card;

  const Contact = (
    <GlassCard style={cardStyle}>
      <Text style={styles.h}>Contact</Text>
      <View style={styles.body}>
        <Kv
          k="Phone"
          v={resident.phoneNumber}
          identifier
          onPress={
            resident.phoneNumber
              ? () => Linking.openURL(`tel:${resident.phoneNumber}`)
              : undefined
          }
        />
        <Kv
          k="Email"
          v={resident.email}
          identifier
          onPress={
            resident.email
              ? () => Linking.openURL(`mailto:${resident.email}`)
              : undefined
          }
          last
        />
      </View>
    </GlassCard>
  );

  const Roommates = (
    <GlassCard style={cardStyle}>
      <Text style={styles.h}>Roommates</Text>
      <View style={[styles.body, styles.col]}>
        {(resident.roommates ?? []).length === 0 ? (
          <Text style={styles.muted}>No roommates</Text>
        ) : (
          (resident.roommates ?? []).map((r: any, i: number) => (
            <PersonCell
              key={r.data?.account?.id ?? i}
              firstName={r.data?.account?.firstName}
              lastName={r.data?.account?.lastName}
              subLine={`${r.data?.account?.unitNumber ?? ''} · linked account`.trim()}
              size="sm"
            />
          ))
        )}
      </View>
    </GlassCard>
  );

  const Lease = (
    <GlassCard style={cardStyle}>
      <Text style={styles.h}>Lease</Text>
      <View style={styles.body}>
        <Kv k="Property" v={resident.propertyName} />
        <Kv k="Unit" v={resident.buildingUnitNumberSpace} />
        <Kv
          k="Move-in"
          v={resident.moveInDate ? formatDateShort(new Date(resident.moveInDate)) : '—'}
        />
        <Kv
          k="Move-out"
          v={resident.moveOutDate ? formatDateShort(new Date(resident.moveOutDate)) : '—'}
        />
        <Kv k="Customer type" v={resident.customerType} last />
      </View>
    </GlassCard>
  );

  const sources = resident.paymentSources ?? [];
  const recurring = resident.recurringPayments ?? [];
  const firstRecurring = recurring[0];

  const Payments = (
    <GlassCard style={cardStyle}>
      <Text style={styles.h}>Payments</Text>
      <View style={styles.body}>
        <Text style={styles.subH}>Autopay</Text>
        <Kv k="Status" v={resident.hasAutopay ? 'Active' : 'Inactive'} />
        {firstRecurring ? (
          <>
            <Kv
              k="Amount"
              v={
                firstRecurring.payFullAmount || firstRecurring.amount == null
                  ? 'Full balance'
                  : `$${Number(firstRecurring.amount).toFixed(2)}`
              }
            />
            <Kv
              k="Starts"
              v={
                firstRecurring.startDate
                  ? formatDateShort(new Date(firstRecurring.startDate))
                  : firstRecurring.createdAt
                  ? formatDateShort(new Date(firstRecurring.createdAt))
                  : '—'
              }
            />
            <Kv
              k="Ends"
              v={
                firstRecurring.endDate
                  ? formatDateShort(new Date(firstRecurring.endDate))
                  : '—'
              }
              last
            />
          </>
        ) : null}

        <Text style={[styles.subH, styles.subHSpaced]}>Methods on file</Text>
        {sources.length === 0 ? (
          <Text style={styles.muted}>No payment methods saved</Text>
        ) : (
          sources.map((s, i) => {
            const label = s.cardType || s.paymentType || s.type || 'Card';
            const last4 = s.last4 ? ` •••• ${s.last4}` : '';
            const value = `${label}${last4}${s.isDefault ? ' (default)' : ''}`;
            return (
              <Kv
                key={s.id ?? i}
                k={s.nickname || `Method ${i + 1}`}
                v={value}
                last={i === sources.length - 1}
              />
            );
          })
        )}
      </View>
    </GlassCard>
  );

  const Recent = (
    <GlassCard style={cardStyle}>
      <View style={styles.hRow}>
        <Text style={styles.h}>Recent Transactions</Text>
        {onViewAllTransactions ? (
          <TouchableOpacity onPress={onViewAllTransactions} accessibilityRole="link">
            <Text style={styles.link}>View all →</Text>
          </TouchableOpacity>
        ) : null}
      </View>
      <View style={styles.body}>
        <TransactionHistorySection
          accountId={leaseCustomerId ?? resident.accountId}
          leaseId={leaseId ?? resident.leaseId}
          residentName={
            resident.firstName && resident.lastName
              ? `${resident.firstName} ${resident.lastName}`
              : undefined
          }
          variant="compact-4"
        />
      </View>
    </GlassCard>
  );

  if (isDesktop) {
    return (
      <View style={styles.grid}>
        <View style={styles.col2Cell}>{Contact}</View>
        <View style={styles.col2Cell}>{Roommates}</View>
        <View style={styles.col2Cell}>{Lease}</View>
        <View style={styles.col2Cell}>{Payments}</View>
        <View style={styles.col2Cell}>{Recent}</View>
      </View>
    );
  }
  return (
    <View style={styles.col}>
      {Contact}
      {Roommates}
      {Lease}
      {Payments}
      {Recent}
    </View>
  );
};

const Kv: React.FC<{
  k: string;
  v?: string | null;
  last?: boolean;
  onPress?: () => void;
  /** Emails and phone numbers keep one line with a middle ellipsis; other values wrap. */
  identifier?: boolean;
}> = ({ k, v, last, onPress, identifier = false }) => {
  const styles = useStyles();
  const inner = (
    <View style={[styles.kv, last && styles.kvLast]}>
      <Text style={styles.kvK}>{k}</Text>
      <Text style={styles.kvV} numberOfLines={identifier ? 1 : 2} ellipsizeMode={identifier ? 'middle' : 'tail'}>
        {v ?? '—'}
      </Text>
    </View>
  );
  return onPress ? <TouchableOpacity onPress={onPress}>{inner}</TouchableOpacity> : inner;
};

const useStyles = () =>
  useThemedStyles(({ components, colors, semantic }) =>
    StyleSheet.create({
      grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'stretch',
      },
      col2Cell: { flexBasis: '48%', flexGrow: 1 },
      col: { flexDirection: 'column', gap: 12 },
      card: {
        padding: 16,
        borderRadius: 14,
      },
      cardStretch: {
        flex: 1,
        alignSelf: 'stretch',
      },
      h: {
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: 0.7,
        color: colors.text.dim,
        fontWeight: '700',
        marginBottom: 12,
      },
      subH: {
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        color: colors.text.dim,
        fontWeight: '600',
        marginBottom: 4,
      },
      subHSpaced: {
        marginTop: 14,
      },
      hRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      body: { flex: 1 },
      kv: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 9,
        borderBottomWidth: 1,
        borderBottomColor: components.admin.dataTable.border,
      },
      kvLast: { borderBottomWidth: 0 },
      kvK: { color: colors.text.dim, fontSize: 12.5, flexShrink: 0 },
      kvV: {
        color: colors.text.primary,
        fontSize: 12.5,
        marginLeft: 12,
        flexShrink: 1,
        textAlign: 'right',
      },
      muted: { color: colors.text.dim, fontSize: 12.5 },
      link: { color: semantic.text.link, fontSize: 11 },
    }),
  );
